import {
  call, put, takeLatest, all, select,
} from 'redux-saga/effects';
import TeeTimeProviderApi from 'apis/supremeGolfApi/TimeSlotsApi';

import { teeTimeFailure } from 'sagas/checkout';
import SelectProviderActions, { SelectProviderTypes } from 'reducers/selectProvider';
import SearchParamsActions from 'reducers/searchParams';
import FlowActions from 'reducers/flow';
import CheckoutActions from 'reducers/checkout';

import DateHelper from 'utils/dateHelper';
import { BOOKING_LIMIT_ERROR_CODE, RATE_LIMIT_ERROR_CODE } from 'utils/constants';
import { NOT_ACTIVE_MEMBERSHIP_ERROR_CODE, NOT_FOR_SALE_CODE } from 'utils/errorCodes';

function getValidTeeTimeProviders(providers, preparedTeeTimes) {
  const result = [];
  providers.forEach((provider, index) => {
    if (!('error' in preparedTeeTimes[index])) {
      result.push({ ...provider, ...preparedTeeTimes[index] });
    }
  });
  return result;
}

const validatePrepareError = (preparedTeeTimes) => {
  const teeTimeError = { status: '', error: null, failureSteps: {} };

  const bookingLimitError = preparedTeeTimes.every((preparedTeeTime) => {
    if ('error' in preparedTeeTime && BOOKING_LIMIT_ERROR_CODE === preparedTeeTime.statusCode) {
      teeTimeError.status = preparedTeeTime.statusCode;
      teeTimeError.error = preparedTeeTime.error;
      teeTimeError.teeTimeId = '';
      return true;
    }
    return false;
  });

  const teeTimeNotAvailable = !bookingLimitError && preparedTeeTimes.every((preparedTeeTime) => {
    const notAvailable = preparedTeeTime?.statusCode && preparedTeeTime?.error;
    const rateLimitReached = RATE_LIMIT_ERROR_CODE === preparedTeeTime.statusCode;

    if (notAvailable || rateLimitReached) {
      teeTimeError.status = preparedTeeTime.statusCode;
      teeTimeError.error = rateLimitReached ? preparedTeeTime.error : null;
      if (Object.keys(preparedTeeTime.failureSteps || {}).length) {
        teeTimeError.failureSteps = preparedTeeTime.failureSteps || {};
        teeTimeError.teeTimeId = preparedTeeTime.teeTimeId || '';
      }
    }

    return notAvailable || rateLimitReached;
  });

  return { bookingLimitError, teeTimeNotAvailable, teeTimeError };
};

function* providerFailure(courseId, params, failureSteps, error = {}) {
  if (!Object.keys(failureSteps || {}).length) return false;
  if (failureSteps?.retryTeeTime) {
    yield put(SelectProviderActions.getProviders(courseId, { ...params, reattempt: true }));
    return true;
  }
  yield put(CheckoutActions.setFailureSteps(failureSteps));
  yield* teeTimeFailure(error?.status || 422, error?.params || {});
  return true;
}

export function* getTeeTimeProviders({ courseId, params }) {
  try {
    const {
      date,
      players: qty,
      selectedHoles: numHoles,
      selectedCart: isRiding,
      timeSlot,
      selectedRateType: rateType,
      isPrepaidOnly,
      reattempt = false,
    } = params;

    const searchParams = {
      courseId,
      date: date && DateHelper.formatDate(date),
      qty,
      isRiding,
      numHoles,
      timeSlot,
      rateType,
      reattempt,
    };

    if (rateType?.majorRateType !== 'lightning') searchParams.isPrepaidOnly = isPrepaidOnly;

    const teeTimeToken = yield select(({ flow }) => flow?.teeTimes?.authToken);
    const rateTypeToken = yield select(({ flow }) => flow?.rateTypes?.authToken);
    const { teeTimes: teeTimeProviders, authToken, failureSteps } = yield call(
      TeeTimeProviderApi.getTeeTimeProviders,
      searchParams,
      rateTypeToken || teeTimeToken,
    );

    yield put(FlowActions.setfProvider({ authToken }));
    sessionStorage.setItem('providerToken', authToken);

    yield put(SelectProviderActions.setIsLoadingProviderList(false));
    let errorObj = { params: { teeTimeId: failureSteps?.teeTimeId || '' } };
    const hasFailure = yield* providerFailure(courseId, params, failureSteps, errorObj);
    if (hasFailure) return;

    if (teeTimeProviders.length === 1 && !teeTimeProviders[0].allowBookingRedirect) {
      yield put(SearchParamsActions.setSkipProvider(true));
      yield put(SearchParamsActions.performCheckoutPreparation(
        teeTimeProviders[0].id,
        teeTimeProviders[0].prepaymentRuleId,
        undefined,
        teeTimeProviders[0].provider,
        teeTimeProviders[0].sourceProvider,
      ));
    } else {
      const preparedTeeTimes = yield all(
        teeTimeProviders.map((provider) => call(TeeTimeProviderApi.prepare, {
          prepaymentRuleId: provider.prepaymentRuleId,
          qty,
          teeTimeId: provider.id,
        })),
      );

      const {
        bookingLimitError,
        teeTimeNotAvailable, teeTimeError,
      } = validatePrepareError(preparedTeeTimes);

      if (bookingLimitError || teeTimeNotAvailable) {
        const { failureSteps: steps, status } = teeTimeError;
        errorObj = { status, params: {} };
        errorObj.params.errorMessage = teeTimeError.error;
        if (teeTimeError?.teeTimeId) errorObj.params.teeTimeId = teeTimeError.teeTimeId;
        const isProviderFailed = yield* providerFailure(courseId, params, steps, errorObj);
        if (isProviderFailed) return;
      }

      const validProviders = getValidTeeTimeProviders(teeTimeProviders, preparedTeeTimes);

      yield put(SelectProviderActions.getProvidersDone(validProviders));
    }
  } catch (error) {
    const { response: { data: { error: errorMessage, failureSteps } = {} } } = error;
    const { response: { status: responseCode } = {} } = error;

    if (errorMessage === 'Authorization Failed') {
      yield put(SelectProviderActions.getProvidersError(error.message));
      yield put(SearchParamsActions.performSearch());
      return;
    }

    if (responseCode === NOT_ACTIVE_MEMBERSHIP_ERROR_CODE) {
      if (params.isRedirectedToMembership) yield put(SearchParamsActions.performRateTypeSearch());
      else yield put(SearchParamsActions.performMembershipSearch());
      return;
    }

    const errorObj = { status: responseCode, params: {} };
    if (responseCode === NOT_FOR_SALE_CODE) errorObj.params.timeSlot = params.timeSlot;
    errorObj.params.errorMessage = errorMessage;
    yield* providerFailure(courseId, params, failureSteps, errorObj);
    yield put(SelectProviderActions.getProvidersError(errorMessage));
  }
}

function* getTeeTimeProvidersWatcher() {
  yield takeLatest(SelectProviderTypes.GET_PROVIDERS, getTeeTimeProviders);
}

export default [
  getTeeTimeProvidersWatcher,
];
